export interface ProfileSaveCompletion<T> {
  baseline: T;
  retry: T | null;
  isDirty: boolean;
}

export type AccountingExecutorPatch = 'self' | 'delegate' | 'none' | (string & {});
export type AccountingSystemPatch = 'freee' | 'moneyforward' | 'excel' | 'other';

export interface AccountingTaskPatch {
  executor: AccountingExecutorPatch;
  system?: AccountingSystemPatch | null;
}

export interface AccountingWherePatch {
  payments?: AccountingTaskPatch | null;
  invoices?: AccountingTaskPatch | null;
  expenses?: AccountingTaskPatch | null;
}

export interface AccountingDraftFields {
  prefAccPaymentsExec: string;
  prefAccPaymentsSystem: string;
  prefAccInvoicesExec: string;
  prefAccInvoicesSystem: string;
  prefAccExpensesExec: string;
  prefAccExpensesSystem: string;
}

export type ProfileDiscardStrategy = 'reset-local' | 'reload-server';

export function resolveProfileDiscardStrategy(retryRequired: boolean): ProfileDiscardStrategy {
  return retryRequired ? 'reload-server' : 'reset-local';
}

export function draftsEqual<T extends object>(a: T, b: T): boolean {
  return (Object.keys(a) as Array<keyof T>).every((key) => a[key] === b[key]);
}

export function resolveProfileSaveCompletion<T extends object>(
  baseline: T,
  current: T,
  attempt: T,
  succeeded: boolean,
): ProfileSaveCompletion<T> {
  const nextBaseline = succeeded ? { ...attempt } : { ...baseline };
  return {
    baseline: nextBaseline,
    retry: succeeded ? null : { ...attempt },
    isDirty: !succeeded || !draftsEqual(current, nextBaseline),
  };
}

export function isKnownAccountingExecutor(value: string): value is 'self' | 'delegate' | 'none' {
  return value === 'self' || value === 'delegate' || value === 'none';
}

export function isPrimaryAccountingExecutor(value: string): value is 'self' | 'none' {
  return value === 'self' || value === 'none';
}

export function isAccountingExecutorPreservedValue(value: string): boolean {
  return value.trim() !== '' && !isPrimaryAccountingExecutor(value);
}

export function buildAccountingWhere(draft: AccountingDraftFields): AccountingWherePatch | null {
  const SYSTEMS = ['freee', 'moneyforward', 'excel', 'other'] as const;
  const makeTask = (execRaw: string, sysRaw: string): AccountingTaskPatch | undefined => {
    if (!execRaw) return undefined;
    const executor = isKnownAccountingExecutor(execRaw) ? execRaw : (execRaw as AccountingExecutorPatch);
    const system = executor === 'self' && (SYSTEMS as readonly string[]).includes(sysRaw)
      ? sysRaw as AccountingSystemPatch
      : null;
    return { executor, ...(system ? { system } : {}) };
  };
  const payments = makeTask(draft.prefAccPaymentsExec, draft.prefAccPaymentsSystem);
  const invoices = makeTask(draft.prefAccInvoicesExec, draft.prefAccInvoicesSystem);
  const expenses = makeTask(draft.prefAccExpensesExec, draft.prefAccExpensesSystem);
  if (!payments && !invoices && !expenses) return null;
  return { ...(payments ? { payments } : {}), ...(invoices ? { invoices } : {}), ...(expenses ? { expenses } : {}) };
}

export function accountingWhereFromProfileValue(rawAccounting: unknown): AccountingWherePatch | null {
  if (typeof rawAccounting === 'string') {
    const SYSTEMS = ['freee', 'moneyforward', 'excel', 'other'] as const;
    const system = (SYSTEMS as readonly string[]).includes(rawAccounting)
      ? rawAccounting as AccountingSystemPatch
      : null;
    return {
      payments: { executor: 'self', ...(system ? { system } : {}) },
      invoices: { executor: 'self', ...(system ? { system } : {}) },
      expenses: { executor: 'self', ...(system ? { system } : {}) },
    };
  }
  if (!rawAccounting || typeof rawAccounting !== 'object' || Array.isArray(rawAccounting)) return null;
  const accounting = rawAccounting as AccountingWherePatch;
  return accounting.payments || accounting.invoices || accounting.expenses ? accounting : null;
}
